import { downloadAuthorizedFile, publicRequest, request } from './core.api';

const PENDING_CHECKOUT_KEY = 'rc_pending_checkout';
const PENDING_BILLING_ACTION_KEY = 'rc_pending_billing_action';

export interface BillingPlanDto {
  key: string;
  displayName: string;
  propertyLimit: number;
  storageLimitBytes: number;
  monthlyPriceId: string;
  yearlyPriceId: string;
  monthlyPriceInCents?: number;
  yearlyPriceInCents?: number;
  currency?: string;
}

export interface BillingStorageUsageDto {
  usedBytes: number;
  limitBytes: number;
  remainingBytes: number;
}

export interface BillingSubscriptionDto {
  paymentProvider: string;
  providerCustomerId?: string | null;
  providerSubscriptionId?: string | null;
  planKey?: string | null;
  planName?: string | null;
  interval?: string | null;
  status?: string | null;
  cancelAtPeriodEnd: boolean;
  currentPeriodEndUtc?: string | null;
  hasManageableSubscription: boolean;
  supportsCustomerPortal: boolean;
  supportsSubscriptionChanges: boolean;
  supportsSubscriptionCancellation: boolean;
  pendingPlanKey?: string | null;
  pendingPlanName?: string | null;
  pendingInterval?: string | null;
  pendingEffectiveAtUtc?: string | null;
  creditBalanceInCents: number;
}

export interface BillingInvoiceDto {
  id: string;
  number?: string | null;
  status: string;
  totalInCents: number;
  amountPaidInCents: number;
  currency: string;
  createdUtc: string;
  paidUtc?: string | null;
  hostedInvoiceUrl?: string | null;
  invoicePdfUrl?: string | null;
}

export interface BillingOverviewDto {
  subscription: BillingSubscriptionDto;
  invoices: BillingInvoiceDto[];
  plans: BillingPlanDto[];
  storageUsage?: BillingStorageUsageDto | null;
  trialStartedAtUtc: string;
  trialEndsAtUtc: string;
  trialExpired: boolean;
  hasApplicationAccess: boolean;
  newSubscriptionAccessEnabled?: boolean;
  canStartNewSubscription?: boolean;
}

export interface CreateCheckoutSessionRequest {
  planKey: string;
  billingInterval: 'monthly' | 'yearly';
}

export interface PendingCheckoutSelection extends CreateCheckoutSessionRequest {}

export interface PendingBillingAction {
  kind: 'checkout' | 'subscription_change';
  planKey: string;
  billingInterval: 'monthly' | 'yearly';
  createdAtUtc: string;
}

export interface CheckoutSessionDto {
  url: string;
}

export interface PortalSessionDto {
  url: string;
}

export interface UpdateBillingSubscriptionResultDto {
  status: 'updated' | 'scheduled' | 'requires_payment';
  url?: string | null;
  amountDueNowInCents: number;
  currency: string;
  effectiveAtUtc?: string | null;
}

export interface CancelBillingSubscriptionResultDto {
  status: 'scheduled' | 'cooling_off_trial_started' | 'cooling_off_profile_only';
  trialEndsAtUtc?: string | null;
}

function parsePendingCheckout(raw: string | null): PendingCheckoutSelection | null {
  if (!raw) {
    return null;
  }

  try {
    const parsed = JSON.parse(raw) as Partial<PendingCheckoutSelection>;
    if (!parsed.planKey || (parsed.billingInterval !== 'monthly' && parsed.billingInterval !== 'yearly')) {
      return null;
    }

    return {
      planKey: parsed.planKey,
      billingInterval: parsed.billingInterval,
    };
  } catch {
    return null;
  }
}

export const billingApi = {
  getOverview: () => request<BillingOverviewDto>('/billing'),
  getPublicPlans: () => publicRequest<BillingPlanDto[]>('/public/billing/plans'),
  downloadInvoice: (pathOrUrl: string, fileName?: string) => downloadAuthorizedFile(pathOrUrl, fileName),
  createCheckoutSession: (body: CreateCheckoutSessionRequest) => request<CheckoutSessionDto>('/billing/checkout-sessions', {
    method: 'POST',
    body: JSON.stringify(body),
  }),
  createPortalSession: () => request<PortalSessionDto>('/billing/portal-sessions', {
    method: 'POST',
    body: JSON.stringify({}),
  }),
  updateSubscription: (body: CreateCheckoutSessionRequest) => request<UpdateBillingSubscriptionResultDto>('/billing/subscriptions/change', {
    method: 'POST',
    body: JSON.stringify(body),
  }),
  cancelSubscription: () => request<CancelBillingSubscriptionResultDto>('/billing/subscriptions/cancel', {
    method: 'POST',
    body: JSON.stringify({}),
  }),
  storePendingCheckout: (selection: PendingCheckoutSelection) => {
    sessionStorage.setItem(PENDING_CHECKOUT_KEY, JSON.stringify(selection));
    localStorage.setItem(PENDING_CHECKOUT_KEY, JSON.stringify(selection));
  },
  getPendingCheckout: (): PendingCheckoutSelection | null => {
    const sessionSelection = parsePendingCheckout(sessionStorage.getItem(PENDING_CHECKOUT_KEY));
    if (sessionSelection) {
      return sessionSelection;
    }

    const persistedSelection = parsePendingCheckout(localStorage.getItem(PENDING_CHECKOUT_KEY));
    if (persistedSelection) {
      sessionStorage.setItem(PENDING_CHECKOUT_KEY, JSON.stringify(persistedSelection));
      return persistedSelection;
    }

    return null;
  },
  clearPendingCheckout: () => {
    sessionStorage.removeItem(PENDING_CHECKOUT_KEY);
    localStorage.removeItem(PENDING_CHECKOUT_KEY);
  },
  storePendingBillingAction: (action: Omit<PendingBillingAction, 'createdAtUtc'>) => {
    sessionStorage.setItem(PENDING_BILLING_ACTION_KEY, JSON.stringify({
      ...action,
      createdAtUtc: new Date().toISOString(),
    }));
  },
  getPendingBillingAction: (): PendingBillingAction | null => {
    const raw = sessionStorage.getItem(PENDING_BILLING_ACTION_KEY);
    if (!raw) {
      return null;
    }

    try {
      const parsed = JSON.parse(raw) as Partial<PendingBillingAction>;
      if (
        (parsed.kind !== 'checkout' && parsed.kind !== 'subscription_change') ||
        !parsed.planKey ||
        (parsed.billingInterval !== 'monthly' && parsed.billingInterval !== 'yearly') ||
        !parsed.createdAtUtc
      ) {
        return null;
      }

      return {
        kind: parsed.kind,
        planKey: parsed.planKey,
        billingInterval: parsed.billingInterval,
        createdAtUtc: parsed.createdAtUtc,
      };
    } catch {
      return null;
    }
  },
  clearPendingBillingAction: () => {
    sessionStorage.removeItem(PENDING_BILLING_ACTION_KEY);
  },
};
